import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';

export const WhatsAppButton = ({ phoneNumber = null }) => {
  const defaultMessage = 'Olá, Gostaria de solicitar um orçamento para Gelos Saborizados (200g).';
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(defaultMessage);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSendMessage = () => {
    if (!phoneNumber) {
      alert('Número do WhatsApp não configurado. Entre em contato pelo formulário.');
      return;
    }

    const text = message || defaultMessage;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
    setMessage(defaultMessage);
  };

  if (!isVisible && !isOpen) return null;

  return (
    <>
      {/* Main Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isVisible ? 1 : 0, opacity: isVisible ? 1 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="fixed bottom-8 right-8 z-50"
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="relative w-16 h-16 bg-gradient-to-r from-[#25D366] to-[#128C7E] rounded-full shadow-2xl shadow-[#25D366]/40 flex items-center justify-center group"
        >
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {isOpen ? (
              <X className="text-white" size={28} />
            ) : (
              <MessageCircle className="text-white" size={28} />
            )}
          </motion.div>

          {/* Pulse Animation */}
          {!isOpen && (
            <motion.div
              className="absolute inset-0 rounded-full bg-[#25D366]"
              initial={{ scale: 1, opacity: 0.5 }}
              animate={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
        </motion.button>
      </motion.div>

      {/* Popup Chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-28 right-8 z-50 w-80 md:w-96"
          >
            <div className="bg-gradient-to-br from-[#0a1628] to-[#0f1f3a] backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageCircle className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Império Ice</h3>
                    <p className="text-white/80 text-sm">Online agora</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="bg-white/5 rounded-2xl p-4 mb-4">
                  <p className="text-white/70 text-sm leading-relaxed">
                    Olá! 👋 Como podemos ajudar você hoje?
                  </p>
                </div>

                {phoneNumber ? (
                  <>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#25D366]/50 transition-colors duration-300 resize-none mb-4"
                      rows="3"
                    />

                    <motion.button
                      onClick={handleSendMessage}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={18} />
                      <span>Enviar no WhatsApp</span>
                    </motion.button>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-white/60 text-sm mb-4">
                      O WhatsApp ainda não foi configurado.
                    </p>
                    <button
                      onClick={() => {
                        document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                        setIsOpen(false);
                      }}
                      className="text-[#7dd3fc] text-sm hover:underline"
                    >
                      Use o formulário de contato
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
